<script lang="ts">
  import * as yup from 'yup';
  import {isValidPhoneNumber} from 'libphonenumber-js/max';
  import {writable} from 'svelte/store';
  import type {Writable} from 'svelte/store';
  import Button from 'Catalyst/components/Button/Button.svelte';
  import CatalystApp from 'Catalyst/components/CatalystApp/CatalystApp.svelte';
  import Form from 'Components/Common/FormManager/Form.svelte';
  import FormControl from 'Catalyst/components/FormControl/FormControl.svelte';
  import PhoneInput from 'Catalyst/components/PhoneInput/PhoneInput.svelte';
  import TextInput from 'Catalyst/components/TextInput/TextInput.svelte';
  import Tooltip from 'Catalyst/components/Tooltip/Tooltip.svelte';
  import SocialLogin from 'Components/SocialLogin/SocialLogin.svelte';
  import {
    personalInfo,
    deal,
    dealExperiments,
    recaptchaToken,
    recaptcha,
    postingInvestor,
    dealMakerUrl,
  } from 'Store/InvestorFlowStore';
  import {validateEmail} from 'Utility/validation';
  import {embeddedWebComponent} from 'Store/embedded/embeddedFlowStore';
  import {whiteLabeling} from 'Store/WhiteLabelStore';
  import type {CssColor} from 'Catalyst/utility/theme';

  export let formContainer: HTMLElement;
  export let initialPhoneCountry: string = 'us';
  export let inputFields = [];
  export let submitInvestor: () => Promise<void> = async () => {};

  let errorList: Writable<Object> = writable({});
  let isValid: boolean = false;
  let showFields: boolean = $deal.feature_flags.investor_conversion_social_login
    ? false
    : true;
  let hideEmailField = false;

  const separateNameFields = $deal.setting.separate_name_fields;
  const requireNameFields = $deal.setting.require_name_fields;
  const phoneNumberRequired = $deal.setting.landing_page_phone_number_required;

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .test('email', 'Please provide a valid email', email =>
        validateEmail(email)
      )
      .required('Email is required'),
    confirmEmail: yup.string().test('confirmEmail', (email, {createError}) => {
      if (
        $dealExperiments?.displayEmailConfirmation &&
        email !== $personalInfo.email
      ) {
        return createError({message: 'Email must match'});
      }
      return true;
    }),
    first_name: yup.string().test('first_name', (value, {createError}) => {
      if (separateNameFields && requireNameFields && !value) {
        return createError({message: 'First name is required'});
      }
      return true;
    }),
    last_name: yup.string().test('last_name', (value, {createError}) => {
      if (separateNameFields && requireNameFields && !value) {
        return createError({message: 'Last name is required'});
      }
      return true;
    }),
    name: yup.string().test('name', (value, {createError}) => {
      if (!separateNameFields && requireNameFields && !value) {
        return createError({message: 'Name is required'});
      }
      return true;
    }),
    phone: yup
      .string()
      .test('phone', 'Invalid phone number', (value, {createError}) => {
        if (phoneNumberRequired && !value) {
          return createError({message: 'Phone number is required'});
        }
        return value ? isValidPhoneNumber(value, 'US') : true;
      }),
  });

  $: if ($personalInfo.email) {
    showFields = true;
  }

  const handleEmailFieldFocus = () => {
    showFields = true;
  };

  $: socialLoginEnabled = $deal.feature_flags.investor_conversion_social_login
    ? $dealExperiments?.displaySocialLogin
    : false;

  $: isRecaptchaEmpty = Boolean($recaptchaToken) || !$recaptcha.render;
  $: disableSubmitButton = !isValid || !isRecaptchaEmpty || $postingInvestor;
  $: displayEmailTooltip = $personalInfo.email.length >= 40;
  $: brandColor = $whiteLabeling.primary_color as CssColor;
</script>

<CatalystApp {brandColor}>
  <Form
    id="personal-info-form"
    validateOnBlur
    schema={validationSchema}
    bind:errorList
    bind:valid={isValid}
  >
    {#if socialLoginEnabled}
      <div
        class="flex flex-col pt-6"
        data-testid="social-login-buttons-container"
      >
        <SocialLogin
          hostURL={$dealMakerUrl?.origin}
          on:submit={() => {
            hideEmailField = true;
            submitInvestor();
          }}
          isEmbedded={$embeddedWebComponent}
        />
        <div class="text-center p-6 social-or" class:hidden={hideEmailField}>
          OR
        </div>
      </div>
    {:else}
      <div class="begin">Begin your investment</div>
    {/if}

    <div
      class="form-group-row-container"
      bind:this={formContainer}
      class:hidden={hideEmailField}
      data-testid="personal-info-input-fields"
    >
      <div class="form-group-row">
        <FormControl forName="email">
          <TextInput
            id="email"
            name="email"
            label="Email"
            invalid={$errorList['email']}
            bind:value={$personalInfo.email}
            autocomplete="email"
            on:focus={handleEmailFieldFocus}
          />
          {#if displayEmailTooltip}
            <Tooltip
              label={$personalInfo.email}
              placement="top"
              data-testid="email-tooltip"
            />
          {/if}
        </FormControl>
        {#if showFields}
          {#if $dealExperiments?.displayEmailConfirmation}
            <FormControl forName="confirmEmail">
              <TextInput
                id="confirmEmail"
                name="confirmEmail"
                label="Confirm Email"
                invalid={$errorList['confirmEmail']}
                bind:value={$personalInfo.email_confirmation}
                autocomplete="email"
              />
            </FormControl>
          {/if}
          {#each inputFields as { name, placeholder, id, testid, autocomplete }}
            <FormControl forName={name}>
              <TextInput
                {id}
                {name}
                label={placeholder}
                invalid={$errorList[name]}
                bind:value={$personalInfo[name]}
                data-testid={testid}
                {autocomplete}
              />
            </FormControl>
          {/each}
          <FormControl forName="phone">
            <PhoneInput
              id="phone"
              name="phone"
              label={`Phone number ${phoneNumberRequired ? '' : '(optional)'}`}
              invalid={$errorList['phone']}
              bind:value={$personalInfo.phone}
              initialCountry={initialPhoneCountry}
            />
          </FormControl>
        {/if}
      </div>

      <slot name="recaptcha" />

      <Button
        label="Continue"
        trailingIcon="arrow_forward"
        size="lg"
        disabled={disableSubmitButton}
        on:click={submitInvestor}
        data-testid="continue-button"
      />
    </div>
    <div class="common-subtitle">
      By clicking "Continue", you consent to receive communications via email or
      SMS pertaining to this offering. You may unsubscribe at any time.
    </div>
  </Form>
</CatalystApp>

<style>
  .form-group-row-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-group-row {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
  }

  .begin {
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 2rem;
    color: var(--dm-text-primary-color, var(--palettes-neutral-800, #344054));
    margin-bottom: 1.5rem;
  }

  .common-subtitle {
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-700, #475467));
    text-align: center;
    margin-top: 1.2rem;
  }

  .hidden {
    display: none;
  }
</style>
